import { saveAdvisorLead } from '../../services/advisorLeadService'
import { getTrackingContext } from '../../services/websiteTracker'

const STORAGE_KEY = 'sma_advisor_conversation_id'

const INTEREST_LABELS = {
  photos: 'Photography',
  videos: 'Videography',
  designs: 'Graphic Design',
  animations: 'Animation',
  ai: 'AI content',
  web: 'Websites & digital experiences',
}

const EXPERIENCE_LABELS = {
  beginner: 'Beginner',
  some: 'Some experience',
  intermediate: 'Intermediate',
  professional: 'Professional',
}

const GOAL_LABELS = {
  career: 'Start a career',
  business: 'Build a business',
  skills: 'Improve my skills',
  content: 'Create content',
  freelance: 'Become a freelancer',
  explore: 'Explore a new skill',
}

export function getOrCreateConversationId() {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY)
    if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing
    const id = crypto.randomUUID()
    sessionStorage.setItem(STORAGE_KEY, id)
    return id
  } catch {
    return crypto.randomUUID()
  }
}

export function emptyLead() {
  return {
    status: 'NEW',
    name: null,
    phone: null,
    offered: false,
    captured: false,
  }
}

export function interestLabel(answers, text = '') {
  if (INTEREST_LABELS[answers?.interest]) return INTEREST_LABELS[answers.interest]
  const blob = `${answers?.courseInterest || ''} ${text}`
  if (/photo/i.test(blob)) return 'Photography'
  if (/video|film/i.test(blob)) return 'Videography'
  if (/design|graphic/i.test(blob)) return 'Graphic Design'
  if (/animat/i.test(blob)) return 'Animation'
  return answers?.courseInterest || null
}

export function experienceLabel(answers, text = '') {
  if (EXPERIENCE_LABELS[answers?.experience]) return EXPERIENCE_LABELS[answers.experience]
  if (/beginner|never used|no experience/i.test(`${answers?.experience || ''} ${text}`)) return 'Beginner'
  return null
}

export function goalLabel(answers) {
  return GOAL_LABELS[answers?.goal] || null
}

export function isContactIntent(text) {
  return /(please contact me|contact me|leave my (details|number|name)|get (back|in touch) (to|with) me|(^|\b)call me\b|whatsapp me)/i.test(
    String(text || '')
  ) && !isHumanIntent(text)
}

export function isHumanIntent(text) {
  return /(talk to (a )?(real )?(person|human|someone|advisor)|speak (to|with) (someone|a person|admissions|a human|an advisor|a real person)|real person|human advisor|talk to admissions|connect me with (sma )?admissions|can someone (help|call) me)/i.test(
    String(text || '')
  )
}

export function isStrongIntent(text) {
  return /(want to (join|enroll|register|apply)|i want this (course|program)|that'?s the course i want|think that'?s the course|ready to (join|enroll|register|apply)|when can i start|where do i pay|want to enroll|how do i (register|apply|join))/i.test(
    String(text || '')
  )
}

export function isCasualOnly(text) {
  return /(where is|location|address|how much|how long|what courses|office hours|slogan|founded|email|website|installment|certificate|own (a |my )?camera)/i.test(
    String(text || '')
  ) && !isContactIntent(text) && !isStrongIntent(text) && !isHumanIntent(text)
}

export function isCtaDecline(text) {
  return /(not (right )?now|maybe later|just looking|i'll think|let me think|no thanks|not yet|not ready to (register|apply))/i.test(
    String(text || '')
  )
}

export function extractPhone(text) {
  const match = String(text || '').match(/(\+?256\d{9}|0\d{9}|\+\d{10,15})/)
  return match ? match[1] : null
}

export function extractName(text, options = {}) {
  const raw = String(text || '')
  const named = raw.match(/\b(?:my name is|this is)\s+([A-Za-z][A-Za-z .'-]{1,79})/i)
  if (named) {
    const name = named[1].replace(/\s+(and|my|whatsapp|phone|number|i want|i would|i'd).*$/i, '').replace(/[.?!]+$/, '').trim()
    if (isPlausiblePersonName(name)) return name
  }
  if (!options.allowBare) return null
  const withoutPhone = raw
    .replace(/(\+?256\d{9}|0\d{9}|\+?\d{9,15})/g, ' ')
    .replace(/[,]/g, ' ')
    .replace(/^(my name is|this is|i am|i'm)\s+/i, '')
    .replace(/[.?!]+$/g, '')
    .trim()
  if (!isPlausiblePersonName(withoutPhone)) return null
  return withoutPhone.slice(0, 80)
}

function isPlausiblePersonName(value) {
  const name = String(value || '').replace(/\s+/g, ' ').trim()
  if (!name || name.length < 2) return false
  if (!/^[A-Za-z][A-Za-z .'-]*$/.test(name)) return false
  if (/\b(ready|beginner|professional|interested|looking|register|apply|enroll|course|photography|student|visitor)\b/i.test(name)) return false
  return true
}

export function firstName(name) {
  return String(name || '').trim().split(/\s+/)[0] || 'there'
}

export function leadPayload(state, extras = {}) {
  const tracking = getTrackingContext()
  return {
    conversationId: state.conversationId,
    name: extras.name || state.answers.leadName || null,
    phone: extras.phone || state.answers.leadPhone || null,
    email: extras.email || state.answers.leadEmail || null,
    courseInterest: extras.courseInterest || interestLabel(state.answers, extras.message),
    recommendedCourseCode: state.answers.recommendedCourseCode || state.recommendation?.code || null,
    experience: extras.experience || experienceLabel(state.answers, extras.message),
    goal: extras.goal || goalLabel(state.answers),
    message: extras.message || null,
    action: extras.action || 'qualify',
    interest: state.answers.interest || null,
    visitorId: tracking.visitorId,
    sessionId: tracking.sessionId,
    utmSource: tracking.utmSource,
    utmMedium: tracking.utmMedium,
    utmCampaign: tracking.utmCampaign,
    utmTerm: tracking.utmTerm,
    utmContent: tracking.utmContent,
    landingPage: typeof window !== 'undefined' ? window.location.pathname : null,
    referrer: typeof document !== 'undefined' ? document.referrer : null,
  }
}

export async function syncLead(state, extras = {}) {
  try {
    const data = await saveAdvisorLead(leadPayload(state, extras))
    return data?.lead || null
  } catch {
    return null
  }
}

export function shouldOfferCapture(state, text) {
  return shouldFollowCaptureStep(state, null, text)
}

export function shouldFollowCaptureStep(state, payload, text) {
  if (state.lead?.captured) return false
  if (state.answers?.leadName && state.answers?.leadPhone) return false
  if (state.answers?.contactDeclined && !isContactIntent(text) && !isStrongIntent(text)) return false

  const step = payload?.captureStep || 'none'
  if (step === 'ask_name' || step === 'ask_phone') return true
  if (step === 'complete' || step === 'declined') return isContactIntent(text) || isStrongIntent(text)
  if (isHumanIntent(text)) return true
  if (isCasualOnly(text)) return isContactIntent(text)
  return isContactIntent(text)
}

export function chatAnswersFromState(state) {
  const step = state.step
  return {
    ...state.answers,
    leadName: state.answers?.leadName || null,
    leadPhone: state.answers?.leadPhone || null,
    leadEmail: state.answers?.leadEmail || null,
    contactDeclined: Boolean(state.answers?.contactDeclined),
    askContactName: step === 'capture_name',
    captureStep: step === 'capture_name' ? 'ask_name' : step === 'capture_phone' ? 'ask_phone' : state.answers?.captureStep || 'none',
  }
}
